import { z } from 'zod/v3';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { toast } from '@cotera/client/app/components';
import { err, ok } from 'neverthrow';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { CocoChatModel } from '../models/coco-chat.model';

/**
 * SubmitToolAction - Generic action for submitting tool results.
 * Parameterized over toolCallId, conversationId, and result.
 * Used by tool renderers to submit completed tool calls.
 *
 * This is the v1/coco version that works with CocoChatModel.
 */
export class SubmitToolAction extends BaseAction<
  {},
  { t: string; message?: string }[]
> {
  static readonly actionId = 'SubmitToolAction';
  title = 'Submit Tool Result';
  inputSchema = z.object({
    toolCallId: z.string(),
    conversationId: z.string(),
    result: z.record(z.unknown()),
  });
  group = 'Chat';

  constructor(
    private readonly client: Pick<
      TenantedClient['resource']['chat'],
      'submitTool'
    >
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(CocoChatModel);
  }

  async execute(
    params: {
      toolCallId: string;
      conversationId: string;
      result: Record<string, unknown>;
    },
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(CocoChatModel)!;

    const result = await this.client.submitTool({
      toolCallIds: [params.toolCallId],
      conversationId: params.conversationId,
      model: model.model,
      result: params.result,
    });

    if (result.isErr()) {
      const errorMessage =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to submit tool result';
      toast.error(errorMessage);
      return err([{ t: 'error', message: errorMessage }]);
    }

    return ok({});
  }
}
